import React, { useEffect, useState } from 'react';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { AISummary, ChildPost, RootPost } from '../../../RootPost';
import styled from 'styled-components/macro';
import HtmlPreview from '../../shared/HtmlPreview';
import Button from '../../shared/Button';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { forMinWidth } from '../../../theme/breakpoints';
import { CommandBarButton } from "@fluentui/react/lib/Button";
import { CommandBar, ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import WasThisHelpful from '../../shared/WasThisHelpful';
import Flex from '../../shared/Flex';
import * as Actions from '../../../store/actions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { Fetchable, Fetchable_InProgress, Fetchable_Success } from '../../../store/appState';
import { UserRole } from '../../../config/permissions';

export interface AISummaryProps {
    ticket: RootPost;
}

export default function AISummaryView({ ticket }: AISummaryProps): JSX.Element {
    const currentUserRoles: UserRole[] = useSelector((state: RootState) => state.appState.userRoles);
    const isAdmin = currentUserRoles && currentUserRoles.indexOf(UserRole.Admins) !== -1;
    const isModerator = currentUserRoles && currentUserRoles.indexOf(UserRole.Moderators) !== -1;
    const [isVisible, setIsVisible] = useState(true);

    const summary: Fetchable<AISummary> = useSelector((state: RootState) => (
        state.appState.summaries[ticket.id]
        ?? (ticket.ai?.summary ? Fetchable_Success(ticket.ai.summary) : null)
        ?? Fetchable_InProgress()
    ));

    const _items: ICommandBarItemProps[] = [
        {
            key: 'regen',
            text: 'Regenerate',
            iconProps: { iconName: 'Refresh' },
            onClick: () => {
                Actions.regenerateSummary(ticket.id, false);
                Actions.sendFeatureFeedback(Actions.FeatureNames.AISummary, Actions.FeatureSentiment.Regenerate, null, summary.data?.id);
            },
        },
        {
            key: 'prompt',
            text: 'Copy Prompt (Mod only)',
            iconProps: { iconName: 'BookAnswers' },
            onClick: () => {
                const prompt = getAIPrompt(ticket);
                navigator.clipboard.writeText(prompt);
                console.log("prompt", prompt);
                alert("Prompt copied to clipboard");
            }
        },
    ];

    const _hideItems: ICommandBarItemProps[] = [
        {
            key: 'hide',
            text: 'Hide',
            onClick: () => {
                setIsVisible(false);
            },
        },
    ];
    useEffect(() => {
        // if no summary on first load, load it.
        if (!ticket.ai?.summary) {
            Actions.regenerateSummary(ticket.id, true);
        }
    }, [ticket.ai]);

    return (isVisible && <Wrapper>
        <Heading>
            <Flex><h2>AI Summary</h2><div className='bullet'>⚠️MS internal only - do not share publicly</div></Flex>

            <Flex>
                {(isAdmin || isModerator) && <CommandBar className='bar' items={_items} styles={{ root: { padding: "0" } }}></CommandBar>}
                {<CommandBar className='bar' items={_hideItems} styles={{ root: { padding: "0" } }}></CommandBar>}
            </Flex>
        </Heading>
        <Description>
            {summary.isFetching && <Flex>
                <Spinner size={SpinnerSize.small} />
                <div className='summarySpin'>Generating summary...</div>
            </Flex>}
            {!summary.isFetching && <>
                <HtmlPreview text={summary.errors?.[0] ?? summary.data.text} isPublic={true} />
            </>}
        </Description>
        {summary.data && <Flex justifyContent='space-between'>
            <div className="text-sm mt-5">AI-generated content may be incorrect. ✅ Summary is up to date</div>
            <Flex justifyContent='flex-end'>
                <WasThisHelpful feature={Actions.FeatureNames.AISummary} aiSummaryId={summary.data.id} />
            </Flex>
        </Flex>}
    </Wrapper>)
}

const MAX_SUMMARY_SINGLE_COMMENT_LENGTH = 5000;
function pesrepareInputSummary(text: string, maxLength: number = MAX_SUMMARY_SINGLE_COMMENT_LENGTH): string {
    if (text === null) {
        return "";
    }

    return text
        .substring(0, maxLength);
}


const LINE_SEPARATOR = "--------";
function getAIPrompt(ticket: RootPost): string {
    let info: string = `This is an issue reported on the Visual Studio feedback forum with relevant discussion. Different parts of is such as title, description, or comments are separated using \`${LINE_SEPARATOR}\`:

Title:
${LINE_SEPARATOR}
${pesrepareInputSummary(ticket.title)}
${LINE_SEPARATOR}

Description:
${LINE_SEPARATOR}
${pesrepareInputSummary(ticket.text)}
${LINE_SEPARATOR}
    
   
`;

    if (ticket.children.length > 0) {
        info += "\nComments:\n";
    }

    for (let i = 0; i < ticket.children.length; i++) {
        info += `
Comment #${i + 1}:
${LINE_SEPARATOR}
${pesrepareInputSummary(ticket.children[i].text)}
${LINE_SEPARATOR}
`;
    }

    info += `
Summarize the issue using the above information and explain all the ways (if any) users are finding the issue, explaining all possible ways users above reproduced the issue. While outputting the text,  highlight key information such as product versions, product names and other key info by using ** and ** as text markers, similar to markdown formatting. Don't repeat the issue title, just make a concise summary.

Try to keep the summary short - a maximum of 2-3 concise sentences for issues under 10 comments. It's OK to have longer summary for issues with more comments.
`;
    return info;
}

const Wrapper = styled.div`
    padding: 1rem 2rem;
    margin-bottom: 2.5rem;
    position: relative;
    background-color: var(--color-background);

    ${forMinWidth.md`
        margin: .5rem 2rem 2.5rem;
    `}

    .text-sm {
        font-size: 1.3rem;
    }

    .mt-5 {
        margin-top: 0.5rem;
    }

    .summarySpin {
        margin-left: 1rem;
    }

    .pin-icon {
        display: block;
        margin: 0 0 1rem 0;
        font-size: 1.5rem;
    }

    .pin-body {
        display: block;
        padding: 2rem 1rem 1rem 1rem;

        ${forMinWidth.md`
            padding: 2rem 2rem 1rem 1rem;
        `}
    }
    .toolbar {
        align-self: stretch;
        min-heidht: 2rem;
    }
    .bar {
    }

    .bullet {
        border-radius: 0.5rem;
        font-size: 0.7em;
        font-weight: bold;
        border: 0.1rem solid var(--color-warning);
        padding: 0 .5rem;
        margin: 0 1rem;
    }
`;

const Heading = styled.div`
    margin: 0 0 1rem 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;
const Description = styled.div`
    border: 0.1rem solid gray;
    border-radius: 0.5rem;
    padding: 1rem;
    word-break: break-word;

    pre, code {
        overflow-wrap: break-all;
        white-space: pre-wrap;
    }
`;

